// Trip, route, itinerary, hotels, vehicles and inclusions sections of the saved-quote view.
import React from 'react';
import { Users, MapPin, ArrowRight, Calendar, Hotel, Car, FileText, Bed, Check, X } from 'lucide-react';
import { C, Card, Group } from './ViewParts';

export const TripSummary = ({ t }) => (
  <Card id="trip" title="Passenger Details" icon={Users}>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[['Guest', t.guest_name], ['Passengers', t.passengers.text], ['Travel Date', t.start_date || '—'], ['Duration', t.duration_text]].map(([k, x]) => (
        <div key={k} className="min-w-0"><p className={C.label}>{k}</p><p className={`${C.body} font-semibold break-words`}>{x}</p></div>
      ))}
    </div>
  </Card>
);

const stopLabel = (r) => (r.kind === 'arrival' ? `${r.name} (Arrival${r.nights ? `/${r.nights}N` : ''})`
  : r.kind === 'departure' ? `${r.name} (Drop)` : r.nights ? `${r.name} (${r.nights}N)` : r.name);

export const TourRoute = ({ stops }) => stops.length > 0 && (
  <Card id="route" title="Tour Route" icon={MapPin}>
    <div className="flex flex-wrap items-center gap-2" data-component="tour-route">
      {stops.map((r, i) => (
        <span key={i} className="inline-flex items-center gap-2 max-w-full">
          <span className="px-3 py-1.5 rounded-full border border-[#D1D5DB] bg-white text-[13px] font-medium text-[#374151] break-words">{stopLabel(r)}</span>
          {i < stops.length - 1 && <ArrowRight className="w-4 h-4 text-gray-400 shrink-0" aria-hidden="true" />}
        </span>
      ))}
    </div>
  </Card>
);

const Overnight = ({ at }) => (at
  ? <span className="inline-block max-w-full break-words px-2 py-0.5 rounded bg-[color-mix(in_srgb,var(--cyan)_15%,white)] text-[var(--navy)] text-[13px] font-medium">{at}</span>
  : <span className="text-gray-400">-</span>);

export const SketchItinerary = ({ days }) => days.length > 0 && (
  <Card id="sketch" title="Sketch Itinerary" icon={Calendar}>
    <Group>
      <div className="overflow-x-auto">
        <table className="w-full" data-component="sketch-itinerary">
          <thead><tr className="bg-[color-mix(in_srgb,var(--navy)_6%,white)] text-left">
            <th className={`${C.th} w-16`}>Day</th><th className={C.th}>Highlights</th><th className={`${C.th} text-center w-32`}>Overnight Stay</th>
          </tr></thead>
          <tbody>{days.map((d) => (
            <tr key={d.day} className="border-t border-[#E5E7EB] align-top">
              <td className={`${C.td} font-semibold !text-[var(--navy)]`}>{d.day}</td>
              <td className={`${C.td} break-words`}>{d.highlights}</td>
              <td className={`${C.td} text-center`}><Overnight at={d.overnight} /></td>
            </tr>))}
          </tbody>
        </table>
      </div>
    </Group>
  </Card>
);

const HotelName = ({ h, cat }) => (
  <span className="font-medium text-[var(--navy)] break-words">{h.hotel_name || 'As per itinerary'}
    {h.borrowed_from && <span data-testid={`view-hotel-borrowed-${cat}-${h.place}`} title={`No ${cat} hotel in ${h.place}`}
      className="ml-1 text-[10px] font-medium rounded px-1.5 py-0.5 bg-amber-100 text-amber-800 border border-amber-300">From {h.borrowed_from}</span>}
  </span>
);

const HotelCards = ({ g }) => (
  <div className="md:hidden divide-y divide-[#E5E7EB]">
    {g.hotels.map((h, i) => (
      <div key={i} className="p-3 space-y-1" data-testid={`view-hotel-card-${g.category}-${i}`}>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[13px] font-semibold text-[var(--navy)] uppercase tracking-wide break-words">{h.place}</span>
          <span className={C.label}>{h.nights_text}</span>
        </div>
        <p className="text-sm"><HotelName h={h} cat={g.category} /></p>
        {h.room && <p className={C.label}>Room: {h.room}</p>}
        <div className={`flex flex-wrap justify-between gap-x-3 ${C.label}`}><span>Rooms: {h.rooms || '—'}</span>{h.dates_text && <span>{h.dates_text}</span>}</div>
      </div>
    ))}
  </div>
);

const HotelTable = ({ g }) => (
  <div className="hidden md:block overflow-x-auto">
    <table className="w-full">
      <thead><tr className="bg-[color-mix(in_srgb,var(--navy)_6%,white)] text-left">
        {['Destination', 'Hotel Name', 'Room Name', 'Rooms', 'Nights', 'Dates'].map((h, i) => <th key={h} className={`${C.th} ${i > 1 ? 'text-center' : ''}`}>{h}</th>)}
      </tr></thead>
      <tbody>{g.hotels.map((h, i) => (
        <tr key={i} className="border-t border-[#E5E7EB] align-top">
          <td className={`${C.td} font-medium`}>{h.place}</td>
          <td className={C.td}><HotelName h={h} cat={g.category} /></td>
          <td className={`${C.td} text-center`}>{h.room || '—'}</td>
          <td className={`${C.td} text-center`} data-testid={`hotel-rooms-${g.category}-${i}`}>{h.rooms || '—'}</td>
          <td className={`${C.td} text-center font-medium`}>{h.nights_text}</td>
          <td className={`${C.td} text-center whitespace-nowrap`}>{h.dates_text || '-'}</td>
        </tr>))}
      </tbody>
    </table>
  </div>
);

export const HotelsByCategory = ({ groups }) => groups.length > 0 && (
  <Card id="hotels" title="Hotels by Category" icon={Hotel}>
    <div className="space-y-4" data-component="hotels-by-category">
      {groups.map((g) => <Group key={g.category} head={g.category}><HotelCards g={g} /><HotelTable g={g} /></Group>)}
    </div>
  </Card>
);

export const VehicleFacts = ({ v }) => v.selected && (
  <Card id="vehicle" title="Vehicle & Transport" icon={Car}>
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {[['Vehicle Type', v.selected.name], ['No. of Vehicles', v.selected.count], ['Transfer Days', `${v.transfer_days || 0} days`]].map(([k, x]) => (
        <div key={k} className="min-w-0"><p className={C.label}>{k}</p><p className={`${C.body} font-semibold break-words`}>{x}</p></div>
      ))}
    </div>
  </Card>
);

export const DayWise = ({ days }) => days.length > 0 && (
  <Card id="detailed" title="Day-wise Itinerary" icon={FileText}>
    <div className="space-y-4" data-component="detailed-itinerary">
      {days.map((d) => (
        <Group key={d.day} head={`Day ${d.day}: ${d.title}`}>
          <div className="p-3 sm:p-4 space-y-3 min-w-0">
            {d.description && <p className={`${C.body} leading-relaxed break-words`}>{d.description}</p>}
            {d.highlights.length > 0 && (
              <div className="flex items-start gap-2 min-w-0"><MapPin className={`${C.icon} mt-0.5`} aria-hidden="true" />
                <p className="min-w-0 break-words"><span className="text-sm font-medium text-[var(--navy)]">Highlights: </span><span className={C.body}>{d.highlights.map(String).join(', ')}</span></p></div>
            )}
            {d.optional_activities.length > 0 && (
              <p className="p-2 rounded border border-[#E5E7EB] break-words"><span className="text-sm font-medium text-[var(--navy)]">Optional Activities: </span>
                <span className={C.body}>{d.optional_activities.map((x) => (typeof x === 'object' ? x.name : x)).join('; ')}</span></p>
            )}
            {d.overnight && (
              <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[#E5E7EB]"><Bed className={C.icon} aria-hidden="true" />
                <span className="text-sm text-[var(--navy)] break-words">Overnight: <strong>{d.overnight}</strong></span></div>
            )}
          </div>
        </Group>
      ))}
    </div>
  </Card>
);

const ListCard = ({ title, items, Icon, color, testId }) => items.length > 0 && (
  <Card title={title} icon={Icon} testId={testId}>
    <ul className="space-y-2">{items.map((x, i) => (
      <li key={i} className={`flex items-start gap-2 ${C.long}`}><Icon className={`w-4 h-4 mt-0.5 shrink-0 ${color}`} aria-hidden="true" /><span className="min-w-0 break-words">{x}</span></li>))}
    </ul>
  </Card>
);

export const InclusionsExclusions = ({ ie }) => (ie.inclusions.length > 0 || ie.exclusions.length > 0) && (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="inclusions-exclusions">
    <ListCard title="Inclusions" items={ie.inclusions} Icon={Check} color="text-success" testId="view-section-inclusions" />
    <ListCard title="Exclusions" items={ie.exclusions} Icon={X} color="text-error" testId="view-section-exclusions" />
  </div>
);
